/**
 * SB6-AUTH-REFRESH-01 — silent refresh-on-401 + replay for raw fetch().
 *
 * The api-client axios instance (`@aeriosiq/api-client`) already has a
 * response interceptor that exchanges the refresh token on a 401 and
 * replays the original request. But several callsites bypass that axios
 * instance and use `fetch()` directly:
 *
 *   - `apps/web/src/features/account/queries/authFetch.ts`   (auth/v1 mutations)
 *   - `apps/lyse/src/features/preferences/authFetch.ts`      (lyse preferences)
 *   - `packages/auth/src/useMe.ts`                           (GET /me)
 *   - `packages/auth/src/useRoleMatrix.ts`                   (GET /role-matrix)
 *
 * They bypass axios because they target a base URL the axios client isn't
 * pinned to (axios baseURL is `/api/lyse/v1`, these need `/api/auth/v1`).
 *
 * Christian's 2026-05-24 walkthrough surfaced the consequence: a token
 * expiry while sitting on `/account-settings`, `/invitations`, or
 * `/users → Invite` produces a user-visible *"couldn't send invitation /
 * couldn't load X"* — because the raw fetch returns 401 and nothing
 * refreshes the token. His coping behaviour was to click
 * `/data-acquisitions` (which triggers `useMe` → triggers some other
 * refresh path) and come back. This module ends the coping behaviour:
 *
 *   const res = await fetchWithRefresh(url, init);   // 401 → refresh → replay
 *
 * Behaviour:
 *   1. Run the original fetch with the caller's headers.
 *   2. If 401, look for a refresh token in the auth store.
 *      - If present, exchange it against Zitadel for a new access token.
 *      - Write the rotated tokens back to the store.
 *      - Re-run the fetch ONCE with the new Authorization header.
 *   3. If the refresh fails (Zitadel rejects, no refresh token, etc.),
 *      clear the session (the caller probably has to redirect to /login).
 *   4. Never retry more than once — avoids tight loops on a stuck token.
 *
 * Concurrency: a single in-flight refresh promise dedupes parallel 401s.
 * If five widgets all 401 in the same tick, exactly one /oauth/v2/token
 * request lands and all five replay with the new token.
 */

import { useAuthStore } from "./store.js";
import { getOidcConfig } from "./config.js";
import { refreshAccessToken } from "./oidc.js";
import { HARD_CAP_MS, IDLE_MAX_MS } from "./sessionLimits.js";

let refreshInFlight: Promise<string | null> | null = null;

/**
 * SB6-AUTH-RECONNECT-01 — decides what happens after a refresh failure:
 *
 *   - hard-cap exceeded                                  → clearSession()
 *   - idle (>= IDLE_MAX) AND refresh-token still present → markSessionExpired()
 *   - active user                                        → clearSession()
 *
 * "Active user but the refresh died" is rare (network blip, Zitadel restart);
 * clearing the session and bouncing to /login is the safer default — an active
 * user can sign back in immediately. The soft modal exists for the case where
 * a user walks away for 8+ hours; their browser still has the FE loaded, and
 * we want them to resume in place.
 */
function handleRefreshFailure(): void {
  const store = useAuthStore.getState();
  const session = store.session;
  const now = Date.now();
  const loggedInAt = session?.loggedInAt;
  const lastActivityAt = session?.lastActivityAt;

  // Hard cap: 30 days since original sign-in. Always clear.
  if (typeof loggedInAt === "number" && now - loggedInAt >= HARD_CAP_MS) {
    store.clearSession();
    return;
  }

  // Idle path: keep the tokens so the soft modal can replay. Only meaningful
  // if we actually have an idle timestamp; missing field == treat as active.
  const idleFor = typeof lastActivityAt === "number" ? now - lastActivityAt : 0;
  if (idleFor >= IDLE_MAX_MS) {
    store.markSessionExpired();
    return;
  }

  // Active user, refresh died: bounce to /login (legacy behaviour).
  store.clearSession();
}

/**
 * Exchange the refresh token for a new access token. Single-in-flight: if
 * called while a refresh is already running, returns the same promise.
 * Used by both `fetchWithRefresh` (reactive, on 401) and
 * `useProactiveTokenRefresh` (proactive, on a timer 60 s before expiry).
 */
export async function tryRefresh(): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight;

  const current = useAuthStore.getState().session;
  if (!current?.refreshToken) {
    return null;
  }

  refreshInFlight = (async (): Promise<string | null> => {
    try {
      const cfg = getOidcConfig();
      const tokens = await refreshAccessToken({
        issuer: cfg.issuer,
        refreshToken: current.refreshToken,
        clientId: cfg.clientId,
      });
      // Write rotated tokens + recompute the access-token deadline. Use
      // setAccessToken (not setSession) so we don't clobber lastActivityAt
      // or loggedInAt — the proactive timer fires every ~hour and we
      // would otherwise reset the original sign-in timestamp on each
      // rotation, defeating the 30-day hard cap.
      const expiresAt = Date.now() + tokens.expires_in * 1000;
      useAuthStore.getState().setAccessToken({
        accessToken: tokens.access_token,
        refreshToken: tokens.refresh_token,
        accessTokenExpiresAt: expiresAt,
      });
      // Successful refresh implicitly clears any prior soft-expired flag.
      useAuthStore.getState().clearSessionExpired();
      return tokens.access_token;
    } catch {
      handleRefreshFailure();
      return null;
    } finally {
      // Reset slot in a microtask so the NEXT 401 (much later) starts a
      // fresh exchange instead of replaying a stale resolved promise.
      queueMicrotask(() => {
        refreshInFlight = null;
      });
    }
  })();

  return refreshInFlight;
}

function setAuthHeader(
  headers: HeadersInit | undefined,
  token: string,
): Headers {
  const merged = new Headers(headers);
  merged.set("Authorization", `Bearer ${token}`);
  return merged;
}

/**
 * Wraps `fetch()` with refresh-on-401 + single replay. Drop-in compatible
 * with `fetch()` — same arguments, same Response return type.
 */
export async function fetchWithRefresh(
  input: RequestInfo | URL,
  init: RequestInit = {},
): Promise<Response> {
  const first = await fetch(input, init);
  if (first.status !== 401) return first;

  const newToken = await tryRefresh();
  if (!newToken) return first; // refresh failed — let the caller see the 401

  const replayInit: RequestInit = {
    ...init,
    headers: setAuthHeader(init.headers, newToken),
  };
  return fetch(input, replayInit);
}

/**
 * Test-only — clears the in-flight refresh slot. Use between tests to
 * keep a stale resolved promise from leaking into a fresh test that
 * expects a real refresh exchange.
 */
export function _resetRefreshInFlightForTests(): void {
  refreshInFlight = null;
}
